/**
 * OrderTicketDialog — opens from TickerWorkbench when user clicks "Trade".
 *
 * Builds an order ticket from a strategy proposal, asks the backend for a
 * deep link to the user's broker, and opens it in a new tab. MarkovLab does
 * NOT submit trades — the user reviews in their broker UI and submits there.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export default function OrderTicketDialog({ open, onClose, ticket }) {
  const [connections, setConnections] = useState([]);
  const [broker, setBroker] = useState("tradier");
  const [qty, setQty] = useState(ticket?.qty || 1);
  const [busy, setBusy] = useState(false);
  const [linkUrl, setLinkUrl] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLinkUrl(null);
    setError(null);
    (async () => {
      try {
        const { data } = await api.get("/broker/me");
        setConnections(data.connections || []);
        if ((data.connections || []).length > 0) setBroker(data.connections[0].broker);
      } catch {
        setError("Could not load broker connections");
      }
    })();
  }, [open]);

  if (!ticket) return null;

  const hasLinked = connections.some(c => c.broker === broker);

  const generate = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post("/broker/order-ticket", {
        broker,
        symbol: ticket.symbol,
        strategy: ticket.strategy,
        short_strike: ticket.short_strike,
        long_strike: ticket.long_strike,
        expiry: ticket.expiry,
        qty,
        premium: ticket.premium,
        short_premium: ticket.short_premium,
        long_premium: ticket.long_premium,
        side: ticket.side || "credit",
      });
      setLinkUrl(data.url);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to build order ticket");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="order-ticket-dialog">
        <DialogHeader>
          <DialogTitle className="tracking-tight">Order Ticket — {ticket.symbol}</DialogTitle>
        </DialogHeader>

            <div className="text-xs grid grid-cols-2 gap-x-4 gap-y-1" style={{ color: "var(--hoip-text-secondary)" }} data-testid="order-ticket-summary">
              <div>Strategy</div><div className="font-mono-data text-right" style={{ color: "var(--hoip-text-primary)" }}>{ticket.strategy}</div>
              {ticket.short_strike != null && (<><div>Short strike</div><div className="font-mono-data text-right" style={{ color: "var(--hoip-text-primary)" }}>${ticket.short_strike}</div></>)}
              {ticket.long_strike != null && (<><div>Long strike</div><div className="font-mono-data text-right" style={{ color: "var(--hoip-text-primary)" }}>${ticket.long_strike}</div></>)}
              {ticket.expiry && (<><div>Expiry</div><div className="font-mono-data text-right" style={{ color: "var(--hoip-text-primary)" }}>{ticket.expiry}</div></>)}
              {ticket.premium != null && (<><div>Credit / debit</div><div className="font-mono-data text-right" style={{ color: "var(--hoip-text-primary)" }}>${ticket.premium}</div></>)}
            </div>

            <div className="flex items-center gap-3 mt-3 text-xs">
              <label className="flex items-center gap-2">
                Broker
                <select value={broker} onChange={e => setBroker(e.target.value)} className="px-2 py-1 border bg-transparent" style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-primary)" }} data-testid="order-ticket-broker">
                  <option value="tradier">Tradier</option>
                  <option value="alpaca">Alpaca</option>
                  <option value="ibkr">IBKR</option>
                </select>
              </label>
              <label className="flex items-center gap-2">
                Qty
                <input type="number" min={1} value={qty} onChange={e => setQty(parseInt(e.target.value) || 1)} className="px-2 py-1 border bg-transparent w-16" style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-primary)" }} data-testid="order-ticket-qty" />
              </label>
              {!hasLinked && (
                <span className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>
                  {broker} not linked → <a href="/profile?tab=brokers" className="underline">link in Profile</a>
                </span>
              )}
            </div>

            {error && <div className="text-xs px-2 py-1" style={{ background: "var(--hoip-loss)", color: "#000" }}>{error}</div>}

            {linkUrl ? (
              <div className="space-y-2">
                <div className="text-[10px] tracking-widest" style={{ color: "var(--hoip-text-tertiary)" }}>READY — review and submit in your broker UI</div>
                <a href={linkUrl} target="_blank" rel="noopener noreferrer" onClick={() => onClose?.()} className="block w-full text-center px-3 py-2 text-xs font-bold tracking-widest" style={{ background: "var(--hoip-profit)", color: "#000" }} data-testid="order-ticket-open-link">OPEN IN {broker.toUpperCase()} ↗</a>
                <p className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>
                  MarkovLab does not place orders. This link prefills the ticket — review carefully and submit yourself.
                </p>
              </div>
            ) : (
              <DialogFooter className="mt-3">
                <button onClick={onClose} className="px-3 py-1.5 text-xs border" style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-secondary)" }}>Cancel</button>
                <button onClick={generate} disabled={busy} className="px-4 py-1.5 text-xs font-bold tracking-widest" style={{ background: "var(--hoip-text-primary)", color: "var(--hoip-bg)" }} data-testid="order-ticket-generate-btn">{busy ? "BUILDING…" : "BUILD ORDER TICKET"}</button>
              </DialogFooter>
            )}
      </DialogContent>
    </Dialog>
  );
}
